import { useEffect, useContext, useMemo, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'hooks';

import { ApplicationFormContext } from 'contexts/ApplicationFormContext';

import getValidationMessage from 'helpers/getValidationMessage';

import { getMandatoryFileValidations } from 'views/shared/ReferToUnderwriting/helpers';

import {
  addMissingMandatoryField,
  removeMissingMandatoryField,
  addRemark,
  addOrUpdateRemark,
  removeRemark,
  updateRemark,
  addFile,
  removeFile,
} from 'reducers/autoPersonalFormReducer';

import { FILE_TYPE } from 'variables/constants';

import { getFieldId } from '../resolverUtils';

const VALIDATION_ID = '023a';
const FORM_STATE_KEY = 'autoPersonalForm' as const;

export type OutOfProvinceAnswer = 'yes' | 'no';
export type CheckType = 'a' | 'b' | 'c';
export type ResolutionMethod = 'loe' | 'drivers_abstract' | 'market_approval';

export interface UseValidation023aResolverConfig {
  section: number;
  itemKey: string;
}

const DISCLOSURE_TEXT =
  'I confirm that the licensing dates shown on my application represent the original date(s) I was first licensed to drive in Ontario, as recorded by the Ministry of Transportation (MTO). ' +
  'I understand that inaccurate or incomplete disclosure of my licensing history may impact rating, eligibility, or coverage.';

export const useValidation023aResolver = (
  config: UseValidation023aResolverConfig,
) => {
  const { section, itemKey } = config;

  const { validations } = useContext(ApplicationFormContext);
  const dispatch = useAppDispatch();

  const mandatoryFileValidations = getMandatoryFileValidations(FORM_STATE_KEY);

  const driverId = itemKey.match(/driver-(\d+)/)?.[1];

  const validation = useMemo(
    () =>
      Array.isArray(validations)
        ? validations.find((v) => v.validation_id === VALIDATION_ID)
        : undefined,
    [validations],
  );

  const checkType = driverId
    ? (validation?.validation_outcome_data?.checks_by_driver?.[driverId] as
        | CheckType
        | undefined)
    : undefined;

  const validationOutcome = validation?.validation_outcome;

  const isFailedForDriver =
    !!driverId &&
    !!validation?.failed_objects?.some((id: number) => String(id) === driverId);

  const alertMessage = getValidationMessage(
    validations,
    VALIDATION_ID,
    driverId ? Number(driverId) : undefined,
  );

  const show =
    !!validation &&
    validation.validation_outcome !== 'pass' &&
    validation.validation_outcome !== 'error' &&
    isFailedForDriver;

  const remark = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].remarks.find(
      (r: any) => r.validation_key === VALIDATION_ID && r.item_key === itemKey,
    ),
  );

  const file = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].files.find(
      (f: any) => f.validation_key === VALIDATION_ID && f.item_key === itemKey,
    ),
  );

  const marketApprovalRemark = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].remarks.find(
      (r: any) =>
        r.validation_key === VALIDATION_ID &&
        r.item_key === `${itemKey}-market`,
    ),
  );

  const marketApprovalFile = useAppSelector((state: any) =>
    state[FORM_STATE_KEY].files.find(
      (f: any) =>
        f.validation_key === VALIDATION_ID &&
        f.item_key === `${itemKey}-market`,
    ),
  );

  const outOfProvinceAnswer: OutOfProvinceAnswer | null =
    remark?.custom_data?.out_of_province_answer ?? null;
  const resolutionMethod: ResolutionMethod | null =
    remark?.custom_data?.resolution_method ?? null;

  const fieldIds = useMemo(
    () => ({
      outOfProvince: getFieldId('Date First Licensed Out of Province', itemKey),
      file: getFieldId('Date First Licensed File', itemKey),
      disclosure: getFieldId('Date First Licensed Disclosure', itemKey),
      resolution: getFieldId('Date First Licensed Resolution', itemKey),
    }),
    [itemKey],
  );

  const initialRemarkText = checkType === 'b' ? DISCLOSURE_TEXT : '';

  // Initialize remark when validation becomes visible
  useEffect(() => {
    if (show) {
      if (checkType === 'b') {
        dispatch(
          addOrUpdateRemark({
            validation_key: VALIDATION_ID,
            item_key: itemKey,
            data: {
              text: DISCLOSURE_TEXT,
              status: 'approved',
              text_validator_log_id: null,
              ai_result: '',
              section,
              custom_data: {
                type: 'date_first_licensed',
                out_of_province_answer: null,
                resolution_method: null,
              },
            },
          }),
        );
        return;
      }

      dispatch(
        addRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          text: initialRemarkText,
          status: 'created',
          text_validator_log_id: null,
          ai_result: '',
          section,
          custom_data: {
            type: 'date_first_licensed',
            out_of_province_answer: null,
            resolution_method: null,
          },
        }),
      );
    } else {
      dispatch(
        removeRemark({ validation_key: VALIDATION_ID, item_key: itemKey }),
      );
      dispatch(
        removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }),
      );
    }
  }, [show, checkType, itemKey, section, dispatch, initialRemarkText]);

  // Auto-approve/reset the parent remark based on resolution completion.
  useEffect(() => {
    if (!show || !remark || outOfProvinceAnswer !== 'yes' || !resolutionMethod)
      return;

    const isComplete =
      resolutionMethod === 'market_approval' ||
      (['loe', 'drivers_abstract'].includes(resolutionMethod) &&
        !!file &&
        (file.status === 'approved' || file.is_deferred));

    if (isComplete && remark.status !== 'approved') {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: { status: 'approved' },
        }),
      );
    } else if (!isComplete && remark.status === 'approved') {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: { status: 'created' },
        }),
      );
    }
  }, [
    show,
    outOfProvinceAnswer,
    resolutionMethod,
    remark,
    file,
    itemKey,
    dispatch,
  ]);

  // Document resolution = LOE or Driver's Abstract upload; only for Check C
  useEffect(() => {
    if (
      show &&
      checkType === 'c' &&
      outOfProvinceAnswer === 'yes' &&
      ['loe', 'drivers_abstract'].includes(resolutionMethod || '')
    ) {
      if (
        file &&
        file.text !==
          (resolutionMethod === 'loe'
            ? 'Letter of Experience'
            : "Driver's Abstract")
      ) {
        dispatch(
          removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }),
        );
      }

      dispatch(
        addFile({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          status: 'created',
          file_validator_log_id: null,
          ai_result: '',
          section,
          type:
            resolutionMethod === 'loe'
              ? FILE_TYPE.TYPE_LOE_023a.type
              : FILE_TYPE.TYPE_023a.type,
          files: [],
          text:
            resolutionMethod === 'loe'
              ? 'Letter of Experience'
              : "Driver's Abstract",
          mandatory: mandatoryFileValidations.includes(VALIDATION_ID),
        }),
      );
    } else {
      dispatch(
        removeFile({ validation_key: VALIDATION_ID, item_key: itemKey }),
      );
    }
  }, [
    show,
    checkType,
    outOfProvinceAnswer,
    resolutionMethod,
    file,
    itemKey,
    section,
    mandatoryFileValidations,
    dispatch,
  ]);

  // Mandatory field tracking
  useEffect(() => {
    if (!show || !remark || checkType === 'a' || validationOutcome === 'fail') {
      dispatch(removeMissingMandatoryField(fieldIds.outOfProvince));
      dispatch(removeMissingMandatoryField(fieldIds.file));
      dispatch(removeMissingMandatoryField(fieldIds.disclosure));
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
      return;
    }

    if (checkType === 'b') {
      dispatch(removeMissingMandatoryField(fieldIds.outOfProvince));
      dispatch(removeMissingMandatoryField(fieldIds.file));
      dispatch(removeMissingMandatoryField(fieldIds.disclosure));
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
      return;
    }

    if (!outOfProvinceAnswer) {
      dispatch(
        addMissingMandatoryField({ name: fieldIds.outOfProvince, section }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.outOfProvince));
    }

    if (outOfProvinceAnswer === 'yes' && !resolutionMethod) {
      dispatch(
        addMissingMandatoryField({ name: fieldIds.resolution, section }),
      );
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.resolution));
    }

    if (
      outOfProvinceAnswer === 'yes' &&
      ['loe', 'drivers_abstract'].includes(resolutionMethod || '') &&
      (!file || (file.status !== 'approved' && !file.is_deferred))
    ) {
      dispatch(addMissingMandatoryField({ name: fieldIds.file, section }));
    } else {
      dispatch(removeMissingMandatoryField(fieldIds.file));
    }

    dispatch(removeMissingMandatoryField(fieldIds.disclosure));
  }, [
    show,
    remark,
    checkType,
    validationOutcome,
    outOfProvinceAnswer,
    resolutionMethod,
    file,
    section,
    fieldIds,
    dispatch,
  ]);

  const handleOutOfProvinceChange = useCallback(
    (answer: OutOfProvinceAnswer) => {
      if (answer === 'no') {
        dispatch(
          removeRemark({
            validation_key: VALIDATION_ID,
            item_key: `${itemKey}-market`,
          }),
        );
        dispatch(
          removeFile({
            validation_key: VALIDATION_ID,
            item_key: `${itemKey}-market`,
          }),
        );
      }

      dispatch(
        addOrUpdateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data:
            answer === 'no'
              ? {
                  text: DISCLOSURE_TEXT,
                  status: 'approved',
                  text_validator_log_id: null,
                  ai_result: '',
                  section,
                  custom_data: {
                    type: 'date_first_licensed',
                    out_of_province_answer: answer,
                    resolution_method: null,
                  },
                }
              : {
                  text: '',
                  status: 'created',
                  text_validator_log_id: null,
                  ai_result: '',
                  section,
                  custom_data: {
                    type: 'date_first_licensed',
                    out_of_province_answer: answer,
                    resolution_method: null,
                  },
                },
        }),
      );
    },
    [dispatch, itemKey, section],
  );

  const handleResolutionMethodChange = useCallback(
    (method: ResolutionMethod) => {
      if (method === 'loe' || method === 'drivers_abstract') {
        dispatch(
          removeRemark({
            validation_key: VALIDATION_ID,
            item_key: `${itemKey}-market`,
          }),
        );
        dispatch(
          removeFile({
            validation_key: VALIDATION_ID,
            item_key: `${itemKey}-market`,
          }),
        );
      }

      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: {
            text: '',
            status: 'created',
            custom_data: {
              type: 'date_first_licensed',
              out_of_province_answer: 'yes',
              resolution_method: method,
            },
          },
        }),
      );
    },
    [dispatch, itemKey],
  );

  const handleRemarksChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        updateRemark({
          validation_key: VALIDATION_ID,
          item_key: itemKey,
          data: { text: e.target.value, status: 'created' },
        }),
      );
    },
    [dispatch, itemKey],
  );

  const isMarketApprovalComplete = useCallback(() => {
    if (!marketApprovalRemark) return false;
    const cd = marketApprovalRemark.custom_data;
    if (cd?.type !== 'market_approval') return false;
    if (!cd.approval_method) return false;
    if (cd.approval_method === 'email') {
      if (
        !marketApprovalFile ||
        (marketApprovalFile.status !== 'approved' &&
          !marketApprovalFile.is_deferred)
      )
        return false;
    }
    if (!cd.approval_date || !cd.approval_time || !cd.underwriter_name)
      return false;
    return true;
  }, [marketApprovalRemark, marketApprovalFile]);

  const getCompactAlertStatus = useCallback(() => {
    if (checkType === 'a' || validationOutcome === 'fail') {
      return 'warning' as const;
    }
    if (checkType === 'b') {
      if (!remark?.text || remark.text.trim().length === 0) {
        return 'warning' as const;
      }
      if (remark.status !== 'approved') return 'warning' as const;
      return 'success' as const;
    }
    if (!remark || !outOfProvinceAnswer) return 'warning' as const;
    if (outOfProvinceAnswer === 'no') return 'success' as const;
    if (outOfProvinceAnswer === 'yes' && !resolutionMethod)
      return 'warning' as const;
    if (
      outOfProvinceAnswer === 'yes' &&
      ['loe', 'drivers_abstract'].includes(resolutionMethod || '') &&
      (!file || (file.status !== 'approved' && !file.is_deferred))
    )
      return 'warning' as const;
    if (
      outOfProvinceAnswer === 'yes' &&
      resolutionMethod === 'market_approval'
    ) {
      return isMarketApprovalComplete()
        ? ('success' as const)
        : ('warning' as const);
    }
    return 'success' as const;
  }, [
    checkType,
    validationOutcome,
    remark,
    outOfProvinceAnswer,
    resolutionMethod,
    file,
    isMarketApprovalComplete,
  ]);

  return {
    state: {
      remark,
      file,
      outOfProvinceAnswer,
      resolutionMethod,
      show,
      checkType,
      validationOutcome,
    },
    handlers: {
      handleOutOfProvinceChange,
      handleResolutionMethodChange,
      handleRemarksChange,
    },
    fieldIds,
    alertMessage,
    getCompactAlertStatus,
  };
};
